package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.StoryRegion
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Browser state for the regional short-stories list. */
data class StoriesUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    /** The stories to show — already narrowed to [selectedRegion] when one is picked. */
    val stories: List<Story> = emptyList(),
    /** Regions that have at least one published story, in enum order — the chip rail. */
    val availableRegions: List<StoryRegion> = emptyList(),
    val selectedRegion: StoryRegion? = null,
)

/**
 * Streams the published short stories, newest first, with a client-side region filter. The rail
 * only offers regions that actually have a story (same rule as the recipe browser), so a chip
 * can never lead to an empty list.
 */
class StoriesViewModel(
    gemsRepository: GemsRepository,
) : ViewModel() {

    private val selectedRegion = MutableStateFlow<StoryRegion?>(null)

    val state: StateFlow<StoriesUiState> =
        combine(gemsRepository.observeStories(), selectedRegion) { result, region ->
            when (result) {
                is DataResult.Success -> {
                    val all = result.data
                    val available = StoryRegion.entries.filter { r -> all.any { it.region == r } }
                    // A region that lost its last story (unpublished) silently falls back to All.
                    val effective = region?.takeIf { it in available }
                    StoriesUiState(
                        loading = false,
                        stories = if (effective == null) all else all.filter { it.region == effective },
                        availableRegions = available,
                        selectedRegion = effective,
                    )
                }
                is DataResult.Failure -> StoriesUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), StoriesUiState())

    /** Tapping the selected chip again clears the filter, like the recipe rail. */
    fun selectRegion(region: StoryRegion?) {
        selectedRegion.value = if (selectedRegion.value == region) null else region
    }
}

/** Detail state for one story, with its like/save/report interactions. */
data class StoryDetailUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val story: Story? = null,
    val liked: Boolean = false,
    val saved: Boolean = false,
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/** Loads one short story (deep-link safe) and drives the same interactions as a tale. */
class StoryDetailViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
    private val storyId: String,
) : ViewModel() {

    private val loaded = MutableStateFlow<DataResult<Story>?>(null)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    init {
        viewModelScope.launch { loaded.value = gemsRepository.getStory(storyId) }
    }

    val state: StateFlow<StoryDetailUiState> =
        combine(
            loaded,
            gemsRepository.observeLikedStoryIds(),
            savedController.saved,
            signInGate,
            reportStatus,
        ) { result, liked, saved, signIn, report ->
            when (result) {
                null -> StoryDetailUiState(loading = true)
                is DataResult.Success -> StoryDetailUiState(
                    loading = false,
                    story = result.data,
                    liked = storyId in liked,
                    saved = saved.any { it.type == GemType.Story && it.id == storyId },
                    signInRequired = signIn,
                    reportStatus = report,
                )
                is DataResult.Failure -> StoryDetailUiState(loading = false, error = true, signInRequired = signIn, reportStatus = report)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), StoryDetailUiState())

    fun toggleLike() {
        val liked = state.value.liked
        viewModelScope.launch {
            val result = gemsRepository.setStoryLiked(storyId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) signInGate.value = true
        }
    }

    fun toggleSave() {
        viewModelScope.launch { savedController.toggle(SavedGem(id = storyId, type = GemType.Story)) }
    }

    fun consumeSignInGate() { signInGate.value = false }

    fun report(reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val r = reportRepository.fileReport("story", storyId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (r.error is AppError.Unauthorized) GemReportStatus.SignInRequired else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() { reportStatus.value = GemReportStatus.Idle }
}
